import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Calendar, TrendingUp, AlertCircle, ArrowLeft } from 'lucide-react'

export default function ResourcePlanningPage() {
  const navigate = useNavigate()
  const [resources, setResources] = useState([
    { id: 1, name: 'Sarah Chen', role: 'Designer', allocation: 100, projects: ['Website Redesign', 'Mobile App'], skills: ['UI/UX', 'Figma'] },
    { id: 2, name: 'Mike Johnson', role: 'Developer', allocation: 80, projects: ['Backend', 'API'], skills: ['Node.js', 'React'] },
    { id: 3, name: 'Emma Wilson', role: 'QA Lead', allocation: 90, projects: ['Testing', 'QA'], skills: ['Test Automation', 'Selenium'] },
    { id: 4, name: 'Alex Kumar', role: 'Backend Dev', allocation: 70, projects: ['API Integration'], skills: ['Python', 'PostgreSQL'] },
  ])

  const stats = useMemo(() => ({
    total: resources.length,
    allocated: resources.filter(r => r.allocation >= 80).length,
    underutilized: resources.filter(r => r.allocation < 50).length,
    avgAllocation: Math.round(resources.reduce((sum, r) => sum + r.allocation, 0) / resources.length)
  }), [resources])

  const getAllocationColor = (allocation) => {
    if (allocation >= 90) return 'bg-red-600/20 text-red-400'
    if (allocation >= 80) return 'bg-yellow-600/20 text-yellow-400'
    if (allocation >= 50) return 'bg-green-600/20 text-green-400'
    return 'bg-blue-600/20 text-blue-400'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/projects')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Project Management
        </button>
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Resource Planning</h1>
          <p className="text-slate-400">Manage team allocation and capacity planning</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Resources</div>
            <div className="text-3xl font-bold text-blue-400">{stats.total}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Fully Allocated</div>
            <div className="text-3xl font-bold text-green-400">{stats.allocated}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Underutilized</div>
            <div className="text-3xl font-bold text-yellow-400">{stats.underutilized}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Avg Allocation</div>
            <div className="text-3xl font-bold text-purple-400">{stats.avgAllocation}%</div>
          </div>
        </div>

        <div className="space-y-4">
          {resources.map((resource) => (
            <div key={resource.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white">{resource.name}</h3>
                  <p className="text-slate-400 text-sm">{resource.role}</p>
                </div>
                <span className={`px-3 py-1 rounded text-xs font-semibold ${getAllocationColor(resource.allocation)}`}>{resource.allocation}% Allocated</span>
              </div>

              <div className="mb-4">
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <div className={`h-full rounded-full bg-gradient-to-r ${resource.allocation >= 90 ? 'from-red-500 to-red-600' : resource.allocation >= 80 ? 'from-yellow-500 to-yellow-600' : 'from-green-500 to-green-600'}`} style={{ width: `${resource.allocation}%` }}></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-slate-400 mb-2">Current Projects</p>
                  <div className="flex flex-wrap gap-2">
                    {resource.projects.map((proj, i) => (
                      <span key={i} className="bg-blue-600/20 text-blue-300 text-xs px-2 py-1 rounded">{proj}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-2">Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {resource.skills.map((skill, i) => (
                      <span key={i} className="bg-green-600/20 text-green-300 text-xs px-2 py-1 rounded">{skill}</span>
                    ))}
                  </div>
                </div>
              </div>

              {resource.allocation >= 100 && (
                <div className="bg-red-600/10 border border-red-600/30 rounded p-3 flex items-start gap-2">
                  <AlertCircle size={16} className="text-red-400 mt-1 flex-shrink-0" />
                  <p className="text-sm text-red-300">Over-allocated. Consider redistributing workload.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
